// voucher.component.ts
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { UrlHistoryService } from '../../services/url.history.service';
import { Voucher } from '../../models/Order';
import {combineLatest, Subscription, timer} from 'rxjs';
import confetti from 'canvas-confetti';
import { NgIf, NgFor } from '@angular/common';
import { CircleButtonComponent } from '../buttons/circle-button.component';
import {VoucherCardComponent} from './voucher-card.component';
import {AppStateService} from '../../services/app-state.service';
import {AppState} from '../../models/AppState';
import {ServingUtil} from '../../utils/serving-util';
import {LoadingSpinnerComponent} from '../ui/loading-spinner-component';
import { PageButtonComponent } from '../buttons/page-button.component';
import MoneyUtils from '../../utils/Utils';
import {VoucherService} from '../../services/voucher.service';
import {FSVoucher} from '../../models/FSVoucher';

@Component({
  selector: 'app-glance-voucher',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    NgFor,
    CircleButtonComponent,
    VoucherCardComponent,
    LoadingSpinnerComponent,
    PageButtonComponent
  ],
  template: `
  <div class="max-w-lg mx-auto flex-col">
    <!-- Loading spinner -->
    <div *ngIf="!activated" class="flex-1 bg-theme-surface min-h-[calc(100svh)] relative overflow-hidden flex flex-col items-center justify-center">
      <app-loading-spinner size="xl"></app-loading-spinner>

      <!-- Retry buttons after 10 seconds -->
      <div *ngIf="showRetryButtons" class="mt-8 flex flex-col items-center gap-4">
        <app-page-button
          [text]="'Försök igen'"
          [icon]="'fa-redo'"
          (onClick)="retry()">
        </app-page-button>
        <app-circle-button
          [icon]="'fa-close'"
          size="12"
          type="solid-border"
          fontSize="xl"
          (onClick)="goBack()">
        </app-circle-button>
      </div>
    </div>

    <!-- Voucher content -->
    <div *ngIf="activated" class="flex-1 bg-theme-surface min-h-[calc(100svh)] relative overflow-hidden">
      <!-- Animated diagonal stripes -->
      <div class="absolute inset-0 bg-stripes" *ngIf="valid"></div>
      <div class="absolute inset-0 bg-gray-200" *ngIf="!valid"></div>

      <div class="relative z-10">
        <div class="absolute top-6 right-6 z-50">
          <app-circle-button [icon]="'fa-close'" size="12" type="solid-border" fontSize="xl" (onClick)="goBack()"></app-circle-button>
        </div>

        <div class="flex items-center w-full justify-center min-h-[calc(100svh)]">
          <app-voucher-card
            [voucher]="activated"
            [logo]="logoUrl"
            [names]="names"
            [valid]="valid"
            [secLeft]="secLeft"
            [circleRaduis]="circleRaduis"
            [circleTitle]="circleTitle"
          ></app-voucher-card>
        </div>
      </div>
    </div>
  </div>
  `,
  styles: [`
    @keyframes moveStripes {
      0% {
        background-position: 0 0;
      }
      100% {
        background-position: 0 -226.27px;
      }
    }

    .bg-stripes {
      background-image: repeating-linear-gradient(
        45deg,
        #727272 0px,
        #727272 80px,
        transparent 80px,
        transparent 160px
      );
      background-size: 226.27px 226.27px;
      animation: moveStripes 2s linear infinite
    }
  `]
})
export class VoucherComponent implements OnInit, OnDestroy {
  private voucherId?: number;
  private voucherIds?: number[];
  private vendorId?: string;
  private timeoutTime!: number;
  private timerSub!: Subscription;
  private sub1?: Subscription;
  private sub2?: Subscription;
  private vouchers: FSVoucher[] = [];
  private shouldStop = false;
  private loadingTimerSub?: Subscription;

  activated!: Voucher;
  valid = true;
  logo1Width = "100%";
  logo2Width = "200px";
  names?: string[];
  appState?: AppState;
  logoUrl?: string;
  secLeft?: string;
  circleRaduis = 95;
  circleTitle?: string;
  showRetryButtons = false;

  constructor(private route: ActivatedRoute, private api: AuthorizedApiService, private identityService: UrlHistoryService,
              private appStateService: AppStateService, private voucherService: VoucherService) { }

  ngOnInit(): void {
    this.sub1 = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap,
      this.appStateService.observeAppState()
    ]).subscribe(([params, qParams, appState]) => {
      const inp = params.get("voucher_id");
      if(inp && inp.indexOf(",")>=0) {
        const parts = inp.split(",");
        this.voucherIds = parts.map(p => Number(p));
      } else {
        this.voucherId = inp ? Number(inp) : undefined;
      }
      this.vendorId = qParams.get("vendorId") || undefined;
      this.appState = appState;

      if (!this.sub2 && MoneyUtils.hasVoucherPriceOverrides(appState?.venueId)) {
        this.sub2 = this.voucherService.observeFilteredVouchers().subscribe(vouchers => {
          this.vouchers = vouchers ?? [];
          this.applyPriceOverride();
        });
      }

      const branding = appState?.cfg.branding;
      if (branding?.logo_badge_file) {
        this.logoUrl = ServingUtil.getDynamicImage(branding.logo_badge_file, 120, false, true);
      }

      this.beginObserving();
    });
  }

  ngOnDestroy(): void {
    this.shouldStop = true;
    this.timerSub?.unsubscribe();
    this.sub1?.unsubscribe();
    this.sub2?.unsubscribe();
    this.loadingTimerSub?.unsubscribe();
  }

  private beginObserving() {
    this.doActivate();
  }

  private doActivate() {
    // Reset retry button state
    this.showRetryButtons = false;

    // Clear any existing loading timer
    this.loadingTimerSub?.unsubscribe();

    // Start 10-second timer to show retry buttons
    this.loadingTimerSub = timer(10000).subscribe(() => {
      if (!this.activated) {
        this.showRetryButtons = true;
      }
    });

    this.api.activateVoucherItem(this.voucherId, this.voucherIds, this.vendorId).subscribe(res => {
      // Clear loading timer on success
      this.loadingTimerSub?.unsubscribe();

      this.activated = res;
      this.applyPriceOverride();
      let name = this.activated.name;
      //name = "§§§|4x Matkupong|1x Alkoholkupong|2x Alkohol Exklusiv|1x Alkoholfritt";
      if (name.startsWith("§§§")) {
        name = name.substring(3);
        this.names = name.split("|");
        this.logo1Width = "50px";
        this.logo2Width = "150px";
      }
      const date = new Date();
      this.timeoutTime = date.getTime() + this.activated.left * 1000;
      this.checkTimeout();
      this.timerSub = timer(0, 1000).subscribe(() => this.checkTimeout());
      if (res.vendor_id != null) {
        const vendorName = this.appState?.cfg.voucher?.vendors?.find(v => v.id === res.vendor_id)?.name;
        if (vendorName) {
          this.circleRaduis = 150;
          this.circleTitle = vendorName;
        }
      }
      else if (this.appState?.cfg.voucher?.collins_voucher_mode === 'modern') {
        this.circleRaduis = 150;
        this.circleTitle = this.appState?.cfg.name;
      }

    });
  }

  private applyPriceOverride() {
    const venueId = this.appState?.venueId;
    if (!MoneyUtils.hasVoucherPriceOverrides(venueId) || !this.activated || !this.valid) {
      return;
    }
    const ids = (this.voucherIds ?? [this.voucherId]).map(id => String(id));
    const selected = this.vouchers.filter(v => ids.includes(v.voucher_id));
    if (selected.length !== ids.length) {
      return;
    }
    const total = selected.reduce((sum, v) => sum + v.price_cent, 0);
    const displayTotal = selected.reduce((sum, v) => sum + MoneyUtils.voucherPriceCent(venueId, v), 0);
    if (displayTotal !== total) {
      this.activated.formatted = MoneyUtils.formatCent(displayTotal);
    }
  }

  private checkTimeout() {
    if (!this.valid) {
      this.secLeft = undefined;
      return;
    }
    const now = new Date();
    this.secLeft = Math.floor((this.timeoutTime - now.getTime()) / 1000).toString();

    if (now.getTime() >= this.timeoutTime) {
      this.activateNotValidState();
    }
  }

  private activateNotValidState() {
    this.activated.name = "Biljett använd";
    this.activated.formatted = "-";
    this.valid = false;
  }

  goBack() {
    this.shouldStop = true;
    this.identityService.navigateBack();
  }

  retry() {
    this.doActivate();
  }

}
